import supabase from '../supabase';

export async function fetchProducts(filters = {}) {
  let query = supabase.from('products').select('*').order('created_at', { ascending: false });
  if (filters.search) {
    query = query.ilike('product_name', `%${filters.search}%`);
  }
  if (filters.category_id && filters.category_id !== 'all') {
    query = query.eq('category_id', filters.category_id);
  }
  if (filters.branch_id) {
    query = query.eq('branch_id', filters.branch_id);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

// Dashboard helpers
export async function countRows(tableName, filter = (q) => q) {
  let query = supabase.from(tableName).select('*', { count: 'exact', head: true });
  const { count, error } = await filter(query);
  if (error) throw error;
  return count || 0;
}

export async function fetchTotalSales() {
  // Sum payment amounts with successful statuses. Adjust statuses to your schema.
  const { data, error } = await supabase
    .from('payments')
    .select('amount,status')
    .in('status', ['success', 'paid', 'captured']);
  if (error) throw error;
  return (data || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
}

export async function fetchRecentOrders(limit = 5) {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  // Normalize fields for the dashboard table
  return (data || []).map((p) => ({
    id: p.id,
    customer: p.customer_name || p.user_name || p.name || 'Customer',
    amount: p.total_amount || p.amount || 0,
    status: (p.status || 'processing').toString().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
  }));
}

export async function fetchTopProductsFromPayments(limit = 3) {
  // Aggregate sales per product_id from payments, then enrich with product names
  const { data, error } = await supabase
    .from('payments')
    .select('product_id,amount')
    .not('product_id', 'is', null);
  if (error) throw error;

  const salesByProduct = new Map();
  for (const row of data || []) {
    const productId = row.product_id;
    const amount = Number(row.amount) || 0;
    const agg = salesByProduct.get(productId) || { sales: 0, revenue: 0 };
    agg.sales += 1;
    agg.revenue += amount;
    salesByProduct.set(productId, agg);
  }

  const top = [...salesByProduct.entries()]
    .sort((a, b) => b[1].revenue - a[1].revenue)
    .slice(0, limit);

  if (top.length === 0) return [];

  const productIds = top.map(([id]) => id);
  const { data: products, error: prodErr } = await supabase
    .from('products')
    .select('id, product_name, name')
    .in('id', productIds);
  if (prodErr) throw prodErr;

  const idToName = new Map((products || []).map(p => [p.id, p.product_name || p.name || `Product ${p.id}`]));
  return top.map(([id, agg]) => ({ id, name: idToName.get(id) || `Product ${id}`, sales: agg.sales, revenue: agg.revenue }));
}

export async function fetchCategories() {
  const { data, error } = await supabase.from('product_category').select('*').order('name');
  if (error) throw error;
  return data || [];
}

export async function fetchProductById(id) {
  const { data, error } = await supabase.from('products').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function updateProduct(id, payload) {
  const { data, error } = await supabase.from('products').update(payload).eq('id', id).select().maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function deleteProduct(id) {
  const { error } = await supabase.from('products').delete().eq('id', id);
  if (error) throw error;
  return true;
}

export async function fetchHighestBid(productId) {
  const { data, error } = await supabase
    .from('bids')
    .select('amount')
    .eq('product_id', productId)
    .order('amount', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.amount ?? null;
}

export async function fetchBranchByName(name) {
  const { data, error } = await supabase.from('branch').select('*').ilike('name', name).maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function fetchProductsByBranch(branchId) {
  const { data, error } = await supabase.from('products').select('*').eq('branch_id', branchId).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createProduct(newProduct) {
  // Try to be schema-tolerant across possible column names
  const payload = {
    product_name: newProduct.name,
    initial_price: newProduct.price ?? null,
    old_price: newProduct.oldPrice ?? null,
    min_auction_amount: newProduct.minAuctionAmount ?? null,
    quantity: newProduct.stock,
    branch_id: newProduct.branch_id ?? '00000000-0000-0000-0000-000000000000', // Default branch UUID
    description: newProduct.description || 'No description provided',
    category_id: newProduct.category_id ?? 1, // Default to first category
  };

  // Insert product
  const { data: product, error: productError } = await supabase
    .from('products')
    .insert(payload)
    .select()
    .maybeSingle();
  
  if (productError) throw productError;

  // If auction_date and status are provided, create an auction entry
  if (newProduct.auction_date && newProduct.branch_id) {
    const auctionPayload = {
      branch_id: newProduct.branch_id,
      auction_date: newProduct.auction_date,
      status: newProduct.status || 'live'
    };

    const { error: auctionError } = await supabase
      .from('auction')
      .insert(auctionPayload);
    
    if (auctionError) {
      console.error('Failed to create auction:', auctionError);
      // Don't throw - product was created successfully
    }
  }

  return product;
}

// Branches CRUD
export async function listBranches() {
  const { data, error } = await supabase.from('branch').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createBranch(branchData) {
  // Support both string (backward compatible) and object input
  let payload;
  
  if (typeof branchData === 'string') {
    // Simple string: set both branch_name and name to the same value
    payload = { 
      branch_name: branchData,
      name: branchData
    };
  } else {
    // Object input: use provided values
    const branchName = branchData.branch_name || branchData.name || branchData;
    payload = { 
      branch_name: branchName,
      name: branchData.name || branchName
    };
  }
  
  console.log('Creating branch with payload:', payload);
  
  const { data, error } = await supabase.from('branch').insert(payload).select().maybeSingle();
  if (error) {
    console.error('Branch creation error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint
    });
    throw error;
  }
  return data;
}

export async function updateBranch(id, updateData) {
  // Update both branch_name and name fields
  const branchName = updateData.branch_name || updateData.name || updateData;
  const payload = {
    branch_name: branchName,
    name: updateData.name || branchName
  };
  
  console.log('Updating branch with payload:', payload);
  
  const { data, error } = await supabase.from('branch').update(payload).eq('id', id).select().maybeSingle();
  if (error) {
    console.error('Branch update error:', error);
    throw error;
  }
  return data;
}

export async function deleteBranch(id) {
  const { error } = await supabase.from('branch').delete().eq('id', id);
  if (error) throw error;
  return true;
}

// Bids API
export async function fetchRecentBids(limit = 10) {
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .order('placed_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchBidsByAuction(auctionId) {
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .eq('auction_id', auctionId)
    .order('placed_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchTotalBids() {
  const { count, error } = await supabase
    .from('bids')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count || 0;
}

export async function fetchAllBids() {
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .order('placed_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchBidById(id) {
  const { data, error } = await supabase
    .from('bids')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function createBid(bidData) {
  const { data, error } = await supabase
    .from('bids')
    .insert([bidData])
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

export async function updateBid(id, updateData) {
  const { data, error } = await supabase
    .from('bids')
    .update(updateData)
    .eq('id', id)
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

export async function deleteBid(id) {
  const { error } = await supabase
    .from('bids')
    .delete()
    .eq('id', id);
  if (error) throw error;
  return true;
}

// Auction with Branch API
export async function fetchAuctionsWithBranch(limit = 10) {
  const { data, error } = await supabase
    .from('auction_with_branch')
    .select('*')
    .order('auction_date', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchAllAuctionsWithBranch() {
  console.log('fetchAllAuctionsWithBranch called');
  
  // First try to get data from the view (which includes branch info)
  const { data, error } = await supabase
    .from('auction_with_branch')
    .select('*')
    .order('auction_date', { ascending: false });
  
  if (error) {
    console.error('Error fetching auction_with_branch:', error);
    throw error;
  }
  
  console.log('auction_with_branch data:', data);
  
  // If the view doesn't include status, fetch it from the auction table
  const auctionIds = (data || []).map(a => a.id);
  console.log('Auction IDs:', auctionIds);
  
  if (auctionIds.length > 0) {
    const { data: statusData, error: statusError } = await supabase
      .from('auction')
      .select('id, status')
      .in('id', auctionIds);
    
    console.log('Status data from auction table:', statusData);
    
    if (!statusError && statusData) {
      // Merge status into view data
      const statusMap = new Map(statusData.map(s => [s.id, s.status]));
      const merged = (data || []).map(auction => ({
        ...auction,
        status: statusMap.get(auction.id) || 'scheduled'
      }));
      console.log('Merged auction data with status:', merged);
      return merged;
    }
  }
  
  console.log('Returning data without status merge');
  return data || [];
}

export async function fetchAuctionsByBranch(branchId) {
  const { data, error } = await supabase
    .from('auction_with_branch')
    .select('*')
    .eq('branch_id', branchId)
    .order('auction_date', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchTotalAuctions() {
  const { count, error } = await supabase
    .from('auction_with_branch')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count || 0;
}

// User Details (Customers) API
export async function fetchAllCustomers() {
  const { data, error } = await supabase
    .from('user_details')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchCustomerById(id) {
  const { data, error } = await supabase
    .from('user_details')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function searchCustomers(searchTerm) {
  const { data, error } = await supabase
    .from('user_details')
    .select('*')
    .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Auctions API
export async function fetchAllAuctions() {
  const { data, error } = await supabase
    .from('auction')
    .select('*, branch:branch_id(id, name, branch_name)')
    .order('auction_date', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchRecentAuctions(limit = 10) {
  const { data, error } = await supabase
    .from('auction')
    .select('*, branch:branch_id(id, name, branch_name)')
    .order('auction_date', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchAuctionById(id) {
  const { data, error } = await supabase
    .from('auction')
    .select('*, branch:branch_id(id, name, branch_name)')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function createAuction(auctionData) {
  const { data, error } = await supabase
    .from('auction')
    .insert([auctionData])
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

export async function updateAuction(id, updateData) {
  const { data, error } = await supabase
    .from('auction')
    .update(updateData)
    .eq('id', id)
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

export async function deleteAuction(id) {
  const { error } = await supabase
    .from('auction')
    .delete()
    .eq('id', id);
  if (error) throw error;
  return true;
}

// User Bank Accounts API
export async function fetchRecentBankAccounts(limit = 10) {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function fetchAllBankAccounts() {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchBankAccountsByUser(userId) {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchTotalBankAccounts() {
  const { count, error } = await supabase
    .from('user_bank_accounts')
    .select('*', { count: 'exact', head: true });
  if (error) throw error;
  return count || 0;
}

export async function fetchBankAccountById(id) {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function createBankAccount(accountData) {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .insert([accountData])
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

export async function updateBankAccount(id, updateData) {
  const { data, error } = await supabase
    .from('user_bank_accounts')
    .update(updateData)
    .eq('id', id)
    .select();
  if (error) throw error;
  return data?.[0] || null;
}

export async function deleteBankAccount(id) {
  const { error } = await supabase
    .from('user_bank_accounts')
    .delete()
    .eq('id', id);
  if (error) throw error;
  return true;
}

// Settings
export async function getSettings() {
  const { data, error } = await supabase.from('settings').select('*').limit(1).maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  return data || {};
}

export async function saveSettings(payload) {
  const existing = await getSettings();
  if (existing?.id) {
    const { data, error } = await supabase.from('settings').update(payload).eq('id', existing.id).select().maybeSingle();
    if (error) throw error;
    return data;
  }
  const { data, error } = await supabase.from('settings').insert(payload).select().maybeSingle();
  if (error) throw error;
  return data;
}

// Auction Status Management
export async function updateAuctionStatusDirect(auctionId, newStatus) {
  console.log('updateAuctionStatusDirect called:', { auctionId, newStatus });
  
  const { data, error } = await supabase
    .from('auction')
    .update({ status: newStatus })
    .eq('id', auctionId)
    .select();
  
  console.log('Supabase update response:');
  console.log('  - Data:', data);
  console.log('  - Data length:', data?.length);
  console.log('  - Error:', error);
  console.log('  - Error details:', error?.message, error?.details, error?.hint);
  
  if (error) {
    console.error('Failed to update auction status:', error);
    throw new Error(`Database error: ${error.message || 'Unknown error'}`);
  }
  
  if (!data || data.length === 0) {
    console.error('❌ No rows updated - Possible reasons:');
    console.error('   1. Row Level Security (RLS) is blocking the update');
    console.error('   2. Auction ID does not exist in database');
    console.error('   3. Missing UPDATE permission on auction table');
    console.error('   4. Status column does not exist');
    throw new Error('No rows updated - auction not found or permission denied');
  }
  
  console.log('✅ Successfully updated auction:', data[0]);
  return data[0];
}

